/**
 * Message Component Design Tokens
 *
 * Maps to PrimeNG's p-message component (from primeng/message).
 * These tokens control the visual styling of inline messages across all severity levels.
 *
 * Token reference: https://primeng.org/message#tokens
 *
 * Severity icon colors are handled in the component template via CSS variable bindings
 * (--m-primary-color, --m-orange-300, --m-red-700, --m-green-700) since PrimeNG's
 * `message.<severity>.color` token affects both text and icon, but our design requires
 * different colors for each.
 */
export const messagesTokens = {
  message: {
    // -------------------------------------------------------------------------
    // Structural tokens (theme-independent)
    // -------------------------------------------------------------------------
    borderRadius: '{borderRadius.sm}',
    borderWidth: '0',
    transitionDuration: '{transition.duration}',

    content: {
      padding: '0.75rem 1rem',
      gap: '0.75rem',
      sm: {
        padding: '0.5rem 1rem'
      },
      lg: {
        padding: '1rem 1.5rem'
      }
    },

    text: {
      fontSize: '0.88rem',
      fontWeight: '400',
      sm: {
        fontSize: '0.81rem'
      },
      lg: {
        fontSize: '1rem'
      }
    },

    icon: {
      size: '1.25rem',
      sm: {
        size: '1rem'
      },
      lg: {
        size: '1.5rem'
      }
    },

    close: {
      button: {
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '{borderRadius.sm}'
      },
      icon: {
        size: '0.75rem'
      }
    },

    // -------------------------------------------------------------------------
    // Color tokens (theme-dependent)
    // -------------------------------------------------------------------------
    colorScheme: {
      light: {
        // Info severity (blue)
        info: {
          background: '{blue.100}',
          borderColor: '{blue.300}',
          color: '{surface.1000}',
          shadow: 'inset 0 0 0 1px {blue.300}, inset 4px 0 0 0 {blue.700}',
          closeButtonHoverBackground: 'transparent',
          closeButtonFocusRingColor: '{primary.color}',
          closeButtonFocusRingShadow: 'none'
        },

        // Warning severity (yellow/orange)
        warn: {
          background: '{yellow.100}',
          borderColor: '{yellow.300}',
          color: '{surface.1000}',
          shadow: 'inset 0 0 0 1px {yellow.300}, inset 4px 0 0 0 {orange.300}',
          closeButtonHoverBackground: 'transparent',
          closeButtonFocusRingColor: '{primary.color}',
          closeButtonFocusRingShadow: 'none'
        },

        // Error severity (red)
        error: {
          background: '{red.100}',
          borderColor: '{red.300}',
          color: '{surface.1000}',
          shadow: 'inset 0 0 0 1px {red.300}, inset 4px 0 0 0 {red.700}',
          closeButtonHoverBackground: 'transparent',
          closeButtonFocusRingColor: '{primary.color}',
          closeButtonFocusRingShadow: 'none'
        },

        // Success severity (green)
        success: {
          background: '{green.100}',
          borderColor: '{green.300}',
          color: '{surface.1000}',
          shadow: 'inset 0 0 0 1px {green.300}, inset 4px 0 0 0 {green.700}',
          closeButtonHoverBackground: 'transparent',
          closeButtonFocusRingColor: '{primary.color}',
          closeButtonFocusRingShadow: 'none'
        }
      }
    }
  }
};

export type MessagesTokens = typeof messagesTokens;
